<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    scrollable
    max-width="1200"
  >
    <v-card height="inherit" style="overflow: auto">
      <!-- Header con radio group para Cliente/Negocio -->
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Buscar Cliente</span>
        <v-radio-group v-model="customerType" row class="ma-0" hide-details>
          <v-radio label="Cliente" value="regular" class="mr-4"></v-radio>
          <v-radio label="Negocio" value="business"></v-radio>
        </v-radio-group>
      </v-card-title>

      <!-- Barra de búsqueda -->
      <div class="py-4 px-6">
        <v-row align="center" class="h-100">
          <v-col cols="12" md="8" class="pr-2">
            <v-text-field
              v-model="searchQuery"
              label="Buscar por nombre, ID, teléfono o correo"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="performSearch"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="d-flex gap-2 pl-2">
            <v-btn
              color="success"
              @click="performSearch"
              :loading="loading"
              class="flex-grow-1"
            >
              <v-icon small left>mdi-magnify</v-icon>
              Buscar
            </v-btn>
            <v-btn
              color="warning"
              @click="clearFilters"
              :disabled="!searchQuery"
            >
              <v-icon class="mr-1">mdi-refresh</v-icon>
              Limpiar
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Grid de resultados -->
      <div  class="py-4 px-6">
        <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

        <!-- Tab Cliente -->
        <div v-if="customerType === 'regular'" :key="`table-regular`">
          <v-data-table
            :headers="headersRegular"
            :items="customers"
            :loading="loading"
            class="elevation-1"
            :items-per-page="10"
            :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
            no-data-text="No se encontraron clientes"
          >
            <!-- Template para botón de selección -->
            <template #item.actions="{ item }">
              <v-btn
                fab
                x-small
                color="primary"
                @click="selectCustomer(item)"
                title="Seleccionar cliente"
              >
                <v-icon color="white">mdi-check</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>

        <!-- Tab Negocio -->
        <div v-else :key="`table-business`">
          <v-data-table
            :headers="headersBusiness"
            :items="customers"
            :loading="loading"
            class="elevation-1"
            :items-per-page="10"
            :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
            no-data-text="No se encontraron negocios"
          >
            <!-- Template para botón de selección -->
            <template #item.actions="{ item }">
              <v-btn
                fab
                x-small
                color="primary"
                @click="selectCustomer(item)"
                title="Seleccionar negocio"
              >
                <v-icon color="white">mdi-check</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- Footer con botones -->
      <v-card-actions class="pt-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog" color="error" small>
          <v-icon small left>mdi-close</v-icon>
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SearchCustomer",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      customerType: "regular",
      searchQuery: "",
      customers: [],
      loading: false,
      search: {
        business: JSON.parse(localStorage.getItem("user"))?.businessid || null,
        branch: JSON.parse(localStorage.getItem("user"))?.branch || null,
        type: 0,
      },
      headersRegular: [
        { text: "Acciones", value: "actions", sortable: false, width: "70px" },
        { text: "ID", value: "ID_Customer", sortable: true, width: "120px" },
        { text: "Nombre Legal", value: "LegalName", sortable: true },
        { text: "Dirección", value: "LegalAddress", sortable: true },
        { text: "Celular", value: "MobileNumber", sortable: true, width: "140px" },
        { text: "Teléfono", value: "PhoneNumber", sortable: true, width: "140px" },
        { text: "Correo", value: "Email", sortable: true },
        { text: "DUI/NIT", value: "LegalNIT", sortable: true, width: "140px" },
      ],
      headersBusiness: [
        { text: "Acciones", value: "actions", sortable: false, width: "70px" },
        { text: "ID", value: "ID_Customer", sortable: true, width: "120px" },
        { text: "Nombre Negocio", value: "BusinessName", sortable: true },
        { text: "Nombre Legal", value: "LegalName", sortable: true },
        { text: "Dirección", value: "BusinessAddress", sortable: true },
        { text: "Celular", value: "BusinessMobileNumber", sortable: true, width: "140px" },
        { text: "Teléfono", value: "BusinessPhone", sortable: true, width: "140px" },
        { text: "Correo", value: "BusinessEmail", sortable: true },
        { text: "NRC", value: "LegalNRC", sortable: true, width: "120px" },
      ],
    };
  },
  watch: {
    customerType() {
      this.clearFilters();
      if (this.customerType === "business") {
        this.search.type = 1;
      } else {
        this.search.type = 0;
      }
      this.loadCustomers();
    },
    value(newVal) {
      if (newVal) {
        this.loadCustomers();
      }
    },
  },
  methods: {
    async loadCustomers() {
      this.loading = true;
      try {
        // Si hay filtro de búsqueda, usar filterCustomers
        if (this.searchQuery) {
          const searchParams = {
            business: this.search.business,
            branch: this.search.branch,
            type: this.search.type,
            filteredFrom: 1,
            showAllRecords: false,
            filter: this.searchQuery,
          };

          const response = await this.$API.customers.filterCustomers(searchParams);
          
          this.customers = Array.isArray(response) ? response : response?.data || [];
        } else {
          // Sin filtros, cargar todos del tipo seleccionado
          const searchParams = {
            business: this.search.business,
            branch: this.search.branch,
            type: this.search.type,
          };
          
          const response = await this.$API.customers.getCustomersPOSCreateInvoice(searchParams);
          
          this.customers = Array.isArray(response) ? response : response?.data || [];
        }
      } catch (error) {
        console.error("Error cargando clientes:", error);
        this.customers = [];
      } finally {
        this.loading = false;
      }
    },
    async performSearch() {
      this.loadCustomers();
    },
    clearFilters() {
      this.searchQuery = "";
      this.loadCustomers();
    },
    selectCustomer(customer) {
      this.$emit("customerSelected", customer);
      this.closeDialog();
    },
    closeDialog() {
      this.$emit("input", false);
    },
  },
  mounted() {
    // El componente debe esperar a que value sea true para cargar
    // Esto se maneja en el watch de value
    if (this.value) {
      this.loadCustomers();
    }
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

/* Mejorar tamaño de fuentes y espacios para pantallas táctiles */
::v-deep .v-data-table {
  font-size: 1rem;
}

::v-deep .v-data-table__wrapper tbody tr {
  height: 52px;
}

::v-deep .v-data-table__wrapper thead tr {
  height: 56px;
}

::v-deep .v-data-table__wrapper td,
::v-deep .v-data-table__wrapper th {
  padding: 12px 8px !important;
}

::v-deep .v-text-field input {
  font-size: 1.05rem;
  padding: 12px 0 !important;
}

::v-deep .v-text-field__details {
  min-height: 20px;
}

.rounded-circle {
  border-radius: 50% !important;
}
</style>
